import type { PresalesDeal } from '@/api/types'

export const formatCurrency = (amount: number | null) => {
  if (!amount) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

export const formatDate = (dateString: string | null) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

export const getFirstName = (fullName: string | null) => {
  if (!fullName) return '-'
  const parts = fullName.trim().split(' ')
  return parts[0]
}

// A deal is "handed over" if the Handover step in the Presales Progress donut
// is done — meaning at some point it was marked as handed over (via the scope
// stepper or HubSpot's handover_to_implementations_date). Keeping this aligned
// with the donut avoids the visual contradiction of a "Handover ✓" donut on a
// deal that's still sitting in the Pre-handover tab.
//
// Signals (any of these = handed over):
//   1. scope_lifecycle_status === 'handover'            (current scope stage)
//   2. handover_date is set                             (HubSpot field OR scope's
//      scopeStatusDates.handover — the same combined signal the donut reads)
//   3. Legacy sales_handover_completed flag             (fallback only)
export const isHandedOver = (
  deal: Pick<PresalesDeal, 'scope_lifecycle_status' | 'sales_handover_completed' | 'handover_date'>,
) => {
  if (deal.scope_lifecycle_status === 'handover' || deal.scope_lifecycle_status === 'implementations') return true
  if (deal.handover_date) return true
  return !!deal.sales_handover_completed
}

export interface HealthResult {
  score: number
  total: number
  percentage: number
  indicators: Array<{ completed: boolean | number; label: string }>
  sowCompletion?: number
}

export const getSalesHealth = (deal: PresalesDeal): HealthResult => {
  const indicators = [
    { completed: deal.sales_demo_request_completed, label: 'Demo Request' },
    { completed: deal.sales_meddpicc_completed, label: 'MEDDPICC' },
    { completed: deal.sales_handover_completed, label: 'Handover' },
  ]
  const completedCount = indicators.filter(i => i.completed).length
  const total = indicators.length

  return {
    score: completedCount,
    total,
    percentage: (completedCount / total) * 100,
    indicators,
  }
}

export const getPresalesHealth = (deal: PresalesDeal): HealthResult => {
  const indicators = [
    { completed: deal.presales_sow_exists, label: 'SOW Exists' },
    { completed: (deal.presales_sow_completion_percent || 0) >= 100, label: 'SOW Complete' },
    { completed: deal.presales_uat_keys_sent, label: 'UAT Keys Sent' },
  ]
  const completedCount = indicators.filter(i => i.completed).length
  const total = indicators.length

  return {
    score: completedCount,
    total,
    percentage: (completedCount / total) * 100,
    indicators,
    sowCompletion: deal.presales_sow_completion_percent || 0,
  }
}

export const getDealTypeBadge = (dealtype: string | null) => {
  if (!dealtype) return null
  if (dealtype === 'newbusiness') return { label: 'New Business', classes: 'bg-emerald-900/30 text-emerald-300 border border-emerald-700/40' }
  if (dealtype === 'Upsell' || dealtype.toLowerCase() === 'upsell') return { label: 'Upsell', classes: 'bg-purple-900/30 text-purple-300 border border-purple-700/40' }
  if (dealtype.toLowerCase().includes('strategic')) return { label: 'Strategic', classes: 'bg-amber-900/30 text-amber-300 border border-amber-700/40' }
  return { label: dealtype, classes: 'bg-gray-800/30 text-gray-400 border border-gray-600/40' }
}

export function getJiraUrl(value: string): string {
  if (value.startsWith('http://') || value.startsWith('https://')) return value
  if (/^[A-Z][A-Z0-9]+-\d+$/i.test(value)) {
    return `https://frankieone.atlassian.net/browse/${value.toUpperCase()}`
  }
  return value
}

export function getJiraLabel(value: string): string {
  const match = value.match(/\/browse\/([A-Z][A-Z0-9]+-\d+)/i)
  if (match) return match[1].toUpperCase()
  if (/^[A-Z][A-Z0-9]+-\d+$/i.test(value)) return value.toUpperCase()
  return value.length > 20 ? value.substring(0, 20) + '...' : value
}

export const getStatusInfo = (deal: PresalesDeal) => {
  if (!deal.is_closed) return { label: 'Open', classes: 'bg-blue-900/30 text-blue-300 border border-blue-700/50' }
  if (deal.closed_won) return { label: 'Won', classes: 'bg-green-900/30 text-green-300 border border-green-700/50' }
  return { label: 'Lost', classes: 'bg-red-900/30 text-red-300 border border-red-700/50' }
}
